import { Alert, Button, Drawer, Form, Skeleton, Space, Modal } from 'antd';
import { useState, useEffect } from 'react'; // Import useState and useEffect
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { SearchQuery } from 'packages/omnitag/state';
import { useGetFiltersListAPI } from 'packages/omnitag/api/use-get-filters-api';
import {
  FiltersForm,
  SearchQueryForm,
} from 'packages/polaris/tool/filters-form';

dayjs.extend(utc); // UTC is a plugin that doesn't come with dayjs by default

export const FiltersPanel = ({
  defaultSearchTerm,
  open,

  onClose,
}: {
  defaultSearchTerm: SearchQuery;
  open: boolean;
  onClose: () => void;
}): JSX.Element => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [form] = Form.useForm<SearchQueryForm | Record<string, any>>();
  const filtersSWR = useGetFiltersListAPI();
  const [previousFormValues, setPreviousFormValues] = useState<
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    SearchQueryForm | Record<string, any>
  >({}); // State to store form values before opening

  useEffect(() => {
    if (open) {
      // When the drawer opens, capture the current form values
      setPreviousFormValues(form.getFieldsValue());
    }
  }, [open, form]);

  const handleDrawerClose = (): void => {
    if (form.isFieldsTouched()) {
      Modal.confirm({
        title: 'Unsaved Filter Changes',
        content: 'Do you want to apply your filter changes before closing?',
        okText: 'Apply Filters',
        cancelText: 'Discard Changes', // Changed text for clarity
        onOk: () => {
          form.submit();
          onClose();
        },
        onCancel: () => {
          form.setFieldsValue(previousFormValues); // Revert to previous state
          onClose();
        },
      });
    } else {
      onClose();
    }
  };

  const handleCancelClick = (): void => {
    form.setFieldsValue(previousFormValues); // Revert to previous state
    onClose();
  };

  return (
    <Drawer
      title="Filters"
      placement="right"
      onClose={handleDrawerClose}
      open={open}
      size="large"
      extra={
        <Space>
          <Button onClick={handleCancelClick}>Cancel</Button>
          <Button
            type="primary"
            htmlType="submit"
            onClick={(): void => form.submit()}
          >
            Update Search
          </Button>
        </Space>
      }
    >
      {filtersSWR.data && (
        <FiltersForm
          dynamicFilters={filtersSWR.data?.filters ?? []}
          form={form}
          defaultSearchTerm={defaultSearchTerm}
          onClose={onClose}
        />
      )}
      {filtersSWR.isLoading && (
        <Skeleton active title={false} paragraph={{ rows: 24 }} />
      )}
      {filtersSWR.error && (
        <Alert
          message={`Could not load filters. ${filtersSWR.error?.message ?? ''}`}
          type="error"
        />
      )}
    </Drawer>
  );
};
